import { useCallback, useRef, useState } from 'react';
import { getLanguage } from '@/i18n/locale';
import { authHeaders } from '@/lib/api-client';
import type { ChatStreamEvent, Intent, Message, RAGSource, TriageLevel } from '@/types';

// ════════════════════════════════════════════════════════════════════
// useChat — owns:
//   - messages: persisted history loaded from /api/sessions OR built up
//                via streaming during this session
//   - streamingText: the text accumulating from the current SSE stream
//   - sessionId: the active session, set from the 'session' SSE event on
//                first message of a new session
//   - lastTriage: the most recent triage level (drives the banner UI)
// ════════════════════════════════════════════════════════════════════

export interface ChatUiMessage extends Message {
  // Local-only flag for messages currently streaming
  pending?: boolean;
}

// Discriminated union for the chat error state. Most errors are
// "something went wrong" generic strings; the rate-limit case
// deserves first-class treatment because it drives the upgrade UI.
export type ChatError =
  | { kind: 'generic'; message: string }
  | { kind: 'rate_limit'; used: number; limit: number };

interface UseChatArgs {
  babyId: string | null;
  initialSessionId?: string | null;
  initialMessages?: Message[];
}

interface SendOptions {
  message: string;
}

export function useChat(args: UseChatArgs) {
  const [messages, setMessages] = useState<ChatUiMessage[]>(args.initialMessages ?? []);
  const [sessionId, setSessionId] = useState<string | null>(args.initialSessionId ?? null);
  const [streamingText, setStreamingText] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<ChatError | null>(null);
  const [lastTriage, setLastTriage] = useState<TriageLevel | null>(null);

  // Holds the AbortController for the in-flight stream so we can cancel.
  const inFlightRef = useRef<AbortController | null>(null);

  const send = useCallback(
    async ({ message }: SendOptions) => {
      if (!args.babyId) {
        setError({ kind: 'generic', message: 'No baby profile selected.' });
        return;
      }
      if (isStreaming) return; // prevent double-send

      setError(null);
      setLastTriage(null);
      setIsStreaming(true);
      setStreamingText('');

      // Optimistically append the user message
      const optimisticUser: ChatUiMessage = {
        id: `local-${Date.now()}`,
        session_id: sessionId ?? '',
        user_id: '',
        role: 'user',
        content: message,
        content_type: 'text',
        image_url: null,
        token_count: null,
        rag_sources: null,
        intent: null,
        triage_level: null,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimisticUser]);

      const controller = new AbortController();
      inFlightRef.current = controller;

      try {
        const headers = {
          'Content-Type': 'application/json',
          ...(await authHeaders()),
        };
        const res = await fetch('/api/chat-stream', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            message,
            session_id: sessionId,
            baby_id: args.babyId,
            // Server uses this for the language of hard-coded safety
            // replies and the disclaimer (country comes from the profile).
            lang: getLanguage(),
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status}: ${txt || res.statusText}`);
        }
        if (!res.body) throw new Error('No response body');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulated = '';
        let finalIntent: Intent = 'general';
        let finalSources: RAGSource[] = [];
        let finalTriage: TriageLevel | null = null;
        let finalMessageId = '';

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by blank lines (\n\n)
          let idx;
          while ((idx = buffer.indexOf('\n\n')) !== -1) {
            const frame = buffer.slice(0, idx);
            buffer = buffer.slice(idx + 2);
            const dataLine = frame.split('\n').find((l) => l.startsWith('data: '));
            if (!dataLine) continue;
            const json = dataLine.slice('data: '.length);
            let event: ChatStreamEvent;
            try {
              event = JSON.parse(json);
            } catch {
              continue;
            }

            switch (event.type) {
              case 'session':
                setSessionId(event.session_id);
                break;
              case 'token':
                accumulated += event.content;
                setStreamingText(accumulated);
                break;
              case 'replace':
                // Server's safety post-filter rewrote the response, or appended
                // a disclaimer. Replace what the user has been watching.
                accumulated = event.content;
                setStreamingText(event.content);
                break;
              case 'triage':
                finalTriage = event.level;
                setLastTriage(event.level);
                break;
              case 'done':
                finalIntent = event.intent;
                finalSources = event.sources;
                finalTriage = event.triage_level;
                finalMessageId = event.message_id;
                break;
              case 'error':
                if (event.error === 'rate_limit_exceeded') {
                  // Parse "used X of Y free messages this month" out of the code.
                  // Falls back to the constants if parsing fails.
                  const match = (event.code ?? '').match(/used (\d+) of (\d+)/);
                  const used = match ? Number(match[1]) : 20;
                  const limit = match ? Number(match[2]) : 20;
                  setError({ kind: 'rate_limit', used, limit });
                  // Roll back the optimistic user message — it never went through.
                  setMessages((prev) => prev.filter((m) => m.id !== optimisticUser.id));
                  setStreamingText('');
                  return;
                }
                throw new Error(event.error + (event.code ? ` — ${event.code}` : ''));
            }
          }
        }

        // Finalize: replace the optimistic user message id (we don't know the real one)
        // and append the assistant message.
        const assistantMsg: ChatUiMessage = {
          id: finalMessageId || `local-${Date.now()}-asst`,
          session_id: sessionId ?? '',
          user_id: '',
          role: 'assistant',
          content: accumulated,
          content_type: 'text',
          image_url: null,
          token_count: null,
          rag_sources: finalSources,
          intent: finalIntent,
          triage_level: finalTriage,
          created_at: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMsg]);
        setStreamingText('');
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          setStreamingText('');
        } else {
          const msg = (err as Error).message ?? 'Something went wrong.';
          setError({ kind: 'generic', message: msg });
          // Roll back the optimistic user message so they can retry
          setMessages((prev) => prev.filter((m) => m.id !== optimisticUser.id));
          setStreamingText('');
        }
      } finally {
        inFlightRef.current = null;
        setIsStreaming(false);
      }
    },
    [args.babyId, sessionId, isStreaming]
  );

  const cancel = useCallback(() => {
    inFlightRef.current?.abort();
  }, []);

  return {
    messages,
    streamingText,
    isStreaming,
    error,
    lastTriage,
    sessionId,
    send,
    cancel,
    setMessages,
  };
}
